// src/pages/IncomeRecord/components/exportIncomeRecordCsv.js
import dayjs from "dayjs";
import _ from "lodash";

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

/**
 * @param {object} params
 * @param {Array}   params.list            - state.incomeRecord.list
 * @param {Array}   params.details         - state.incomeRecord.details
 * @param {Array}   params.listWithDetails - state.incomeRecord.listWithDetails
 * @param {object}  params.filters         - state.incomeRecord.filters
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportIncomeRecordCsv = ({
  list,
  details,
  listWithDetails,
  filters,
  title    = "Income Record Report",
  filename = "income_record",
}) => {
  let headers = [];
  let dataRows = [];
  let grandTotal = 0;

  // ── By Income ──────────────────────────────────────────────────────────────
  if (filters.filterType === "By Income") {
    headers = [
      "No.", "VCH No", "Date", "Income From", "Receipt By", "Amount",
    ].map(escape);

    dataRows = details.map((row, i) => [
      i + 1,
      row.inc_code    || "",
      row.created_date ? dayjs(row.created_date).format("YYYY-MMM-DD") : "",
      row.acc_name    || "",
      row.full_name   || "",
      fmt(row.inc_amount),
    ].map(escape));

    grandTotal = _.sumBy(details, (r) => Number(r.inc_amount) || 0);

    const totalRow = [
      "Grand Total:", "", "", "", "", fmt(grandTotal),
    ].map(escape);

    const rows = [[title], [], headers, ...dataRows, [], totalRow];
    return triggerDownload(rows, filename);
  }

  // ── With Details ───────────────────────────────────────────────────────────
  if (filters.recordType === "with details") {
    headers = [
      "No.", "VCH", "Date", "Receipt Into", "Receipt By", "Detail Account", "Amount",
    ].map(escape);

    listWithDetails.forEach((row, i) => {
      const subRows = row.details || [];
      subRows.forEach((d, di) => {
        dataRows.push([
          di === 0 ? i + 1 : "",                                           // No. only on first detail row
          di === 0 ? (row.inc_code   || "") : "",
          di === 0 ? (row.created_date ? dayjs(row.created_date).format("YYYY-MMM-DD") : "") : "",
          di === 0 ? (row.acc_name   || "") : "",
          di === 0 ? (row.full_name  || "") : "",
          d.acc_name  || "",
          fmt(d.inc_amount),
        ].map(escape));
      });

      // sub-total row
      const subTotal = _.sumBy(subRows, "inc_amount");
      dataRows.push(["", "", "", "", "", "Sub Total:", fmt(subTotal)].map(escape));
      dataRows.push([]); // blank separator
    });

    grandTotal = _.sumBy(listWithDetails, (r) => Number(r.inc_total) || 0);

    const totalRow = [
      "Grand Total:", "", "", "", "", "", fmt(grandTotal),
    ].map(escape);

    const rows = [[title], [], headers, ...dataRows, totalRow];
    return triggerDownload(rows, filename);
  }

  // ── Without Details (default) ──────────────────────────────────────────────
  headers = [
    "No.", "VCH No", "Date", "Receipt Into", "Receipt By", "Total Amount",
  ].map(escape);

  dataRows = list.map((row, i) => [
    i + 1,
    row.inc_code     || "",
    row.created_date ? dayjs(row.created_date).format("YYYY-MMM-DD") : "",
    row.acc_name     || "",
    row.full_name    || "",
    fmt(row.inc_total),
  ].map(escape));

  grandTotal = _.sumBy(list, (r) => Number(r.inc_total) || 0);

  const totalRow = [
    "Grand Total:", "", "", "", "", fmt(grandTotal),
  ].map(escape);

  const rows = [[title], [], headers, ...dataRows, [], totalRow];
  triggerDownload(rows, filename);
};

// ── helper ─────────────────────────────────────────────────────────────────
const triggerDownload = (rows, filename) => {
  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};